<script setup lang="ts">
import { computed } from 'vue';
import PlayerTypeIcon from '@/components/DataTable/PlayerTypeIcon.vue';
import { useZoneIcon } from '@/composables/useZoneIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { LocationZonePlayerBrandFields } from '@/composables/api/useLocationZonePlayerInspectorApi';
import { useNowTicker } from '@/composables/useNowTicker';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import RecordLink from '@/components/RecordLink.vue';
import ZoneDisplayString from '@/components/ZoneDisplayString.vue';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { useQuickSearchMatches } from '@/composables/quickSearchMatches';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';
import { readZoneDisplayString, splitZoneDisplayString } from '@/utils/zoneDisplayString';
import { checkinStatusColor, formatCompactDuration, secondsSince } from '@/utils/checkinStatus';
import { formatUsMmDdYyTime12h } from '@/utils/date';

/**
 * Fixed card header for Location Zone Player search cards. These fields are
 * always shown regardless of Column Settings: check-in status light
 * (green when online — last check-in within ONLINE_WITHIN_MINUTES — red when
 * offline or never), the id (gvadmin link), location + zone names, and a live
 * time-since-last-check-in.
 *
 * The flattened player_data_updated_at is live: the search API re-flattens it
 * from MySQL on every request (it is only ~5 min stale inside the Elasticsearch
 * document, which drives filtering/sorting).
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        /**
         * The quick-search fields this row matched (SAU-591), passed in by the
         * search page. Each is highlighted where the header draws it, and the
         * line that holds it wraps. `cardShownMatchFields` lists the same
         * fields, and the two must stay in step.
         */
        matchedFields?: readonly string[];
    }>(),
    { matchedFields: () => [] }
);

const nowMs = useNowTicker();

function readDisplayString(value: unknown): string | null {
    if (value && typeof value === 'object' && 'display_string' in value) {
        const display = (value as { display_string?: unknown }).display_string;
        return typeof display === 'string' && display.trim() !== '' ? display : null;
    }
    return null;
}

const lastCheckinAt = computed<string | null>(() => {
    const flattened = props.row.player_data_updated_at;
    return typeof flattened === 'string' && flattened !== '' ? flattened : null;
});

const checkinSeconds = computed(() => secondsSince(lastCheckinAt.value, nowMs.value));
const statusColor = computed(() => checkinStatusColor(checkinSeconds.value));

const statusDotClass = computed(
    () =>
        ({
            green: 'bg-green-500',
            red: 'bg-red-500',
        })[statusColor.value]
);

const statusLabel = computed(() =>
    checkinSeconds.value === null
        ? 'Never checked in'
        : `Last check-in ${formatCompactDuration(checkinSeconds.value)} ago`
);

/**
 * Whether the heading above already names the location.
 *
 * The same split the heading uses, asked as a question. When it succeeds the heading reads
 * "Hill Inc-Lebsack Group" with both halves linked, and row two carries the account alone —
 * repeating the location there put the client on the card twice.
 *
 * When it does not succeed the heading shows the zone name only, so row two goes back to
 * "location · account". That covers a player with a location but no zone, a zone indexed
 * before the location prefix existed, and a zone genuinely not named after its location —
 * in every one of those the location is the card's only clue to which client this is.
 */
const headingCarriesLocation = computed<boolean>(
    () =>
        splitZoneDisplayString(
            readZoneDisplayString(props.row.location_zone),
            readZoneDisplayString(props.row.location)
        ).location !== null
);

// The id, the account and the row-two location link to their own pages here, each with a
// side-peek button. The heading's zone and location links live inside ZoneDisplayString,
// which owns the split heading.
const { canOpen } = useOpenRecordInInspector();

function relationId(value: unknown): number | null {
    if (value && typeof value === 'object' && 'id' in value) {
        const id = (value as { id?: unknown }).id;
        return typeof id === 'number' ? id : null;
    }
    return null;
}

const locationName = computed(() => readDisplayString(props.row.location));
const locationId = computed(() => relationId(props.row.location));
const locationInspectable = computed(() => locationId.value !== null && canOpen('Location'));
const accountName = computed(() => readDisplayString(props.row.account));
const accountId = computed(() => relationId(props.row.account));
const accountInspectable = computed(() => accountId.value !== null && canOpen('Account'));
const playerInspectable = computed(() => typeof props.row.id === 'number' && canOpen('LocationZonePlayer'));

const quickSearch = useQuickSearchMatches();
const highlightTerms = computed(() => quickSearch?.terms.value ?? []);
function isMatched(field: string): boolean {
    return props.matchedFields.includes(field);
}

/** Whether the heading's zone name opens with the location nested in the zone reference. */
const headingCarriesZoneLocation = computed<boolean>(() => {
    const zone = props.row.location_zone;
    const zoneLocation = zone && typeof zone === 'object' ? (zone as { location?: unknown }).location : null;
    return (
        splitZoneDisplayString(readZoneDisplayString(zone), readZoneDisplayString(zoneLocation)).location !== null
    );
});

/** The location is drawn in the heading when the heading carries it, and on row two when not. */
const headingHoldsMatch = computed(
    () =>
        isMatched('location_zone.display_string') ||
        (isMatched('location.display_string') && headingCarriesLocation.value) ||
        (isMatched('location_zone.location.display_string') && headingCarriesZoneLocation.value)
);
const secondRowHoldsMatch = computed(
    () =>
        (isMatched('location.display_string') && !headingCarriesLocation.value) || isMatched('account.display_string')
);

/** Hover text for row two, matching whatever that row actually renders. */
const secondRowTitle = computed<string | undefined>(() => {
    const parts = [
        headingCarriesLocation.value ? null : (locationName.value ?? 'Unknown location'),
        accountName.value,
    ].filter((part): part is string => Boolean(part));

    return parts.length > 0 ? parts.join(' · ') : undefined;
});

// The zone's mark next to its name: master, subscriber or plain zone, by the
// shared rule (see useZoneIcon).
const zoneIcon = useZoneIcon(() => props.row.location_zone);

// Device-reported software version, shown under the check-in line. Normalize a leading
// "v" so we render a single "v" prefix; omitted entirely when the device hasn't reported one.
const playerVersion = computed<string | null>(() => {
    const version = props.row.player_data_version;
    if (typeof version !== 'string') {
        return null;
    }
    const trimmed = version.trim().replace(/^v/i, '');
    return trimmed === '' ? null : trimmed;
});

// Player type nests as {id, display_string} in the ES row; the icon only needs the id.
const playerTypeId = computed<number | null>(() => {
    const playerType = props.row.player_type;
    if (playerType && typeof playerType === 'object' && 'id' in playerType) {
        const id = (playerType as { id?: unknown }).id;
        return typeof id === 'number' ? id : null;
    }
    return null;
});

// Server-computed brand — absent on rows from a backend without SAU-259. The
// type icon is always muted; the brand only names itself in its label.
const playerBrand = computed<string | null>(() => {
    const brand = (props.row as LocationZonePlayerBrandFields).brand;
    return typeof brand === 'string' ? brand : null;
});
</script>

<template>
    <!--
      Two aligned rows so each rail glyph is vertically centered with its own text
      line: check-in dot with the id + zone heading, player-type icon with the
      account line. The zone heading is the full display string "{location}-{zone}"
      with each half linked separately, so the account can have row two to itself
      instead of repeating a location the heading already names.
    -->
    <!-- The body rows below (summary, Currently Playing cover, tag chips) sit
         flush with the card's left edge, sharing it with the rail icons here —
         the card reads as one left edge; only the header text sits right of
         its rail glyph. -->
    <div class="grid grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-x-1 gap-y-0.5">
        <div class="flex justify-center">
            <Tooltip>
                <TooltipTrigger as-child>
                    <span
                        class="inline-block size-2.5 rounded-full"
                        :class="statusDotClass"
                        role="img"
                        :aria-label="statusLabel"
                        data-testid="checkin-status-dot"
                        :data-status="statusColor"
                    />
                </TooltipTrigger>
                <TooltipContent side="top" align="start">{{ statusLabel }}</TooltipContent>
            </Tooltip>
        </div>

        <div class="flex min-w-0 items-baseline gap-2">
            <span class="shrink-0 font-mono text-sm font-semibold tabular-nums" data-testid="card-player-id">
                <!-- `inline`: the side-peek button takes no room until hover, so the
                     heading beside the id does not move over on every card. -->
                <RecordLink
                    v-if="playerInspectable"
                    entity-id="LocationZonePlayer"
                    :record-id="Number(row.id)"
                    :label="`player ${row.id}`"
                    inline
                    data-testid="card-player-id-inspect"
                    ><SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="isMatched('id')" whole>{{
                        row.id
                    }}</SearchHighlight></RecordLink
                >
                <SearchHighlight v-else :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="isMatched('id')" whole>{{
                    row.id
                }}</SearchHighlight>
            </span>
            <component
                :is="zoneIcon"
                v-if="zoneIcon"
                class="size-3.5 shrink-0 self-center"
                aria-hidden="true"
                data-testid="card-zone-icon"
            />
            <!-- The heading carries BOTH records: "Hill Inc" opens the Location, "Lebsack
                 Group" opens the Zone. It reads exactly as the display string always did,
                 which is what lets the line below drop the location without losing the
                 link that line used to carry. While it holds a quick-search match it
                 wraps instead of clipping, so the highlight is visible. -->
            <h3
                class="min-w-0 text-base font-semibold leading-tight"
                :class="headingHoldsMatch ? 'wrap-anywhere' : 'truncate'"
                data-testid="card-player-heading"
            >
                <SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="headingHoldsMatch">
                    <ZoneDisplayString :zone="row.location_zone" :location="row.location" />
                </SearchHighlight>
            </h3>
        </div>

        <div class="text-right">
            <Tooltip v-if="checkinSeconds !== null">
                <TooltipTrigger as-child>
                    <span class="cursor-default font-mono text-sm tabular-nums" data-testid="checkin-elapsed">
                        {{ formatCompactDuration(checkinSeconds) }}
                    </span>
                </TooltipTrigger>
                <TooltipContent side="top" align="end" class="space-y-0.5">
                    <p class="text-xs font-medium">Last check-in</p>
                    <p class="text-muted-foreground text-xs tabular-nums">
                        {{ formatUsMmDdYyTime12h(lastCheckinAt) }}
                    </p>
                </TooltipContent>
            </Tooltip>
            <span v-else class="text-muted-foreground text-xs" data-testid="checkin-elapsed">never</span>
        </div>

        <div class="flex justify-center">
            <PlayerTypeIcon :player-type-id="playerTypeId" :brand="playerBrand" />
        </div>

        <!-- Row two: the account, preceded by the location ONLY when the heading above did
             not already name it (see headingCarriesLocation). -->
        <p
            class="text-muted-foreground min-w-0 text-sm leading-snug"
            :class="secondRowHoldsMatch ? 'wrap-anywhere' : 'truncate'"
            :title="secondRowTitle"
            data-testid="card-player-second-row"
        >
            <SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="secondRowHoldsMatch">
                <!-- Both names are links in running text inside a truncating line, so each
                     uses RecordLink's `inline` mode: an inline-flex box would be dropped
                     whole instead of clipped with "…" (see ZoneDisplayString). -->
                <template v-if="!headingCarriesLocation">
                    <RecordLink
                        v-if="locationInspectable"
                        entity-id="Location"
                        :record-id="locationId!"
                        :label="`location ${locationName ?? locationId}`"
                        inline
                        data-testid="card-location-inspect"
                        >{{ locationName ?? 'Unknown location' }}</RecordLink
                    >
                    <template v-else>{{ locationName ?? 'Unknown location' }}</template>
                </template>
                <template v-if="accountName">
                    <template v-if="!headingCarriesLocation">{{ ' · ' }}</template>
                    <RecordLink
                        v-if="accountInspectable"
                        entity-id="Account"
                        :record-id="accountId!"
                        :label="`account ${accountName}`"
                        inline
                        data-testid="card-account-inspect"
                        >{{ accountName }}</RecordLink
                    >
                    <template v-else>{{ accountName }}</template>
                </template>
            </SearchHighlight>
        </p>

        <p
            v-if="playerVersion"
            class="text-muted-foreground text-right font-mono text-xs leading-snug tabular-nums"
            data-testid="player-version"
            :title="`Software version ${playerVersion}`"
        >
            v{{ playerVersion }}
        </p>
        <span v-else />
    </div>
</template>
